import { useEffect, useState } from 'react';

type QueryFunction<T> = () => Promise<T>;

interface UseQueryResult<T> {
  data: T | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useQuery<T>({
  queryKey,
  queryFn,
}: {
  queryKey: string[];
  queryFn: QueryFunction<T>;
}): UseQueryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      setError(null);
      const result = await queryFn();
      setData(result);
    } catch (err) {
      setIsError(true);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...queryKey]);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch: fetchData,
  };
}